import * as XLSX from "xlsx"
import type { Lead, NewLeadInput, LeadStatus } from "../types/lead"

// Everything here lives in the browser's localStorage - there is no backend/server
// involved, per project constraint. That means leads are only visible on the device/
// browser they were added from. Use "Export to Excel" regularly as a backup, and see
// the lead-gen proposal for how to move this to a real shared database later.
const STORAGE_KEY = "afb_leads_v1"

function readAll(): Lead[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writeAll(leads: Lead[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(leads))
}

function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export const leadsStorage = {
  getAll(): Lead[] {
    return readAll().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  },

  add(input: NewLeadInput): Lead {
    const now = new Date().toISOString()
    const lead: Lead = {
      ...input,
      id: generateId(),
      status: "new",
      createdAt: now,
      updatedAt: now,
    }
    const leads = readAll()
    leads.push(lead)
    writeAll(leads)
    return lead
  },

  updateStatus(id: string, status: LeadStatus): void {
    const leads = readAll()
    const index = leads.findIndex((l) => l.id === id)
    if (index === -1) return
    leads[index] = { ...leads[index], status, updatedAt: new Date().toISOString() }
    writeAll(leads)
  },

  remove(id: string): void {
    writeAll(readAll().filter((l) => l.id !== id))
  },

  exportToExcel(leads: Lead[]): void {
    const headers = [
      "Name",
      "Phone",
      "City",
      "Interest",
      "Budget",
      "Timeline",
      "Source",
      "Status",
      "Notes",
      "Added On",
    ]
    const rows = leads.map((l) => [
      l.name,
      l.phone,
      l.city,
      l.interest,
      l.budget,
      l.timeline,
      l.source,
      l.status,
      l.notes,
      new Date(l.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
    ])
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    XLSX.utils.book_append_sheet(wb, ws, "Leads")
    XLSX.writeFile(wb, `anmol-leads-${new Date().toISOString().split("T")[0]}.xlsx`)
  },
}
